import React, { Fragment, ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import styled from 'styled-components';
import Utils from '../../shared/utils';
import CampaignUsageBadge from '../../yourborrowbox/marketing/campaigns/CampaignUsageBadge';
import { CAMPAIGN_PRODUCTS, useFeature } from '../../shared/utils/togglz';
import ProductCover from '../../shared/common/ProductCover';
import ProductLanguage from '../../shared/product/ProductLanguage';
import ProductCampaignEndDate from '../../shared/product/ProductCampaignEndDate';
import SelectButton from '../../shared/common/SelectButton';
import { PreviewButton } from '../../shared/preview/PreviewButton';
import ProductSeries from '../../shared/product/ProductSeries';
import ProductAuthors from '../../shared/product/ProductAuthors';
import ProductNarrators from '../../shared/product/ProductNarrators';
import ProductGenres from '../../shared/product/ProductGenres';
import ProductAge from '../../shared/product/ProductAge';
import { ProductReleaseDate } from '../../shared/product/ProductReleaseDate';
import ProductPublisher from '../../shared/product/ProductPublisher';
import { ProductFormat } from '../../shared/product/ProductFormat';
import { TileBadges } from '../../shared/common/BadgesV2';

import { CampaignProductStatistics } from './CampaignProductStatistics';
import { ProductListItemProps } from '../../shared/common/ProductList';
import { AddProduct } from '../../shared/common/AddProduct/AddProduct';
import MetadataUtils from '../../shared/utils/utils-metadata';
import { hasProductSelection } from '../../shared/product-selection/ProductSelectionUtils';
import { concreteLoanFormatNameToObject } from '../../shared/loanFormat/mappers';

const StProductTile = styled.div`
    height: 560px !important;
`;

const StDetails = styled.div<{
    readonly hovered?: boolean;
}>`
    min-height: ${props => (props.hovered ? 'initial' : '105px !important')};
`;

const StCampaignUsageBadge = styled(CampaignUsageBadge)`
    margin-bottom: 5px;
`;

type Props = ProductListItemProps & {
    readonly shopLanguage: string | undefined;
};

const onSelectPrice = async () => {};

export const CampaignProductTile = ({
    actionButtons,
    hoverListener,
    loanFormat: loanFormatKey,
    minimalView: minimalViewProp,
    onAddPrice,
    onProductImageClick,
    onSelectProduct,
    product,
    productSelection,
    shopLanguage,
    isProductSelectable,
    isProductSelected
}: Props) => {
    const [showDetails, setShowDetails] = useState(false);

    function handleShowDetails() {
        setShowDetails(true);
        if (hoverListener) {
            hoverListener(true);
        }
    }

    function hideDetails() {
        setShowDetails(false);
        if (hoverListener) {
            hoverListener(false);
        }
    }

    const isCampaignsEnabled = useFeature(CAMPAIGN_PRODUCTS);
    const productLoanFormat = concreteLoanFormatNameToObject(product.loanFormat);
    const productLoanFormatKey = productLoanFormat?.key;
    const productLoanFormatSingularName = productLoanFormat?.singularName;
    const shortenedTitle = Utils.truncate(product.title);

    let loanFormatClass = 'prd-tile';
    if (minimalViewProp) {
        loanFormatClass += ' minimal';
    }

    // hover state
    const tileClasses = classNames('prd-tile-inner', {
        over: showDetails
    });

    let productLink = '#';
    if (!hasProductSelection(productSelection)) {
        productLink = `/products/${product.id}`;
    }

    const productCoverLink = (
        <Link
            to={productLink}
            onClick={e => {
                if (onProductImageClick) {
                    e.preventDefault();
                    onProductImageClick();
                }
            }}
        >
            <ProductCover onMouseEnter={handleShowDetails} minSizeInPx={300} product={product} />
        </Link>
    );

    const minimalView = minimalViewProp && !showDetails;

    let campaignUsageBadge: ReactNode = null;
    let productCampaignEndDate: ReactNode = null;

    if (isCampaignsEnabled) {
        const isPreOrder = MetadataUtils.isProductPreOrderable(product);
        campaignUsageBadge = (
            <StCampaignUsageBadge
                loanFormat={productLoanFormatSingularName!}
                usageLimit={product.usageLimit}
                campaignTerm={product.termLimit}
                isSimultaneousAccess={product.simultaneousAccess}
                releaseDate={product.releaseDate}
                isPreOrder={isPreOrder}
            />
        );
        if (product.campaignDetails && product.campaignDetails.salesEndDate) {
            productCampaignEndDate = <ProductCampaignEndDate endDate={product.campaignDetails.salesEndDate} />;
        }
    }

    return (
        <StProductTile className={loanFormatClass} onMouseLeave={hideDetails}>
            <div className={tileClasses}>
                <div className="packshot" data-displayname="ProductTile" data-testid="ProductTile">
                    {productCoverLink}
                    <SelectButton
                        isProductSelectable={isProductSelectable}
                        isProductSelected={isProductSelected}
                        productSelection={productSelection}
                        onClick={onSelectProduct}
                    />
                    <PreviewButton product={product} />
                </div>

                <div className="details" data-displayname="ProductTileDetails">
                    <StDetails hovered={showDetails}>
                        <ProductSeries
                            product={product}
                            onMouseEnter={handleShowDetails}
                            loanFormatKey={loanFormatKey}
                            shopLanguage={shopLanguage}
                        />
                        <h3 onMouseEnter={handleShowDetails}>
                            <Link to={productLink}>{shortenedTitle}</Link>
                        </h3>
                        <ProductAuthors
                            product={product}
                            onMouseEnter={handleShowDetails}
                            loanFormatKey={loanFormatKey}
                            shopLanguage={shopLanguage}
                        />
                    </StDetails>
                    <div className="details-inner expanded">
                        <ProductNarrators
                            product={product}
                            loanFormatKey={productLoanFormatKey}
                            shopLanguage={shopLanguage}
                        />
                        <ProductGenres product={product} loanFormatKey={loanFormatKey} shopLanguage={shopLanguage} />
                        <ProductAge product={product} loanFormatKey={loanFormatKey} shopLanguage={shopLanguage} />
                        <ProductLanguage product={product} loanFormatKey={loanFormatKey} shopLanguage={shopLanguage} />
                        <ProductReleaseDate product={product} />
                        {productCampaignEndDate}
                        <ProductPublisher product={product} loanFormatKey={loanFormatKey} shopLanguage={shopLanguage} />
                        <ProductFormat product={product} loanFormatKey={loanFormatKey} shopLanguage={shopLanguage} />
                    </div>
                    {campaignUsageBadge}
                </div>

                <div className="prd-bottom" data-displayname="ProductTileBottom">
                    <TileBadges
                        product={product}
                        loanFormatKey={loanFormatKey}
                        showAll={showDetails}
                        shopLanguage={shopLanguage}
                    />
                    {minimalView ? null : (
                        <>
                            <CampaignProductStatistics
                                copyStockAmount={product.copyStockAmount}
                                stockDetails={product.stockDetails}
                                preOrderStockDetails={product.preOrderStockDetails}
                                campaignStockAmount={product.campaignStockAmount}
                                loanFormatKey={productLoanFormatKey!}
                                copiesInCart={product.copiesInCart}
                            />
                            {actionButtons}
                            <AddProduct
                                product={product}
                                onAddPrice={onAddPrice}
                                onSelectPrice={onSelectPrice}
                                showSelectedPriceOnly={false}
                                allowSelection={true}
                            />
                        </>
                    )}
                </div>
            </div>
        </StProductTile>
    );
};
